export type LeadMagnet = {
  slug: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  title: string;
  format: string;
  persona: string;
  promise: string;
  answerFirst: string;
  bodySections: readonly ResourceBodySection[];
  calculator?: ResourceCalculator;
  inside: readonly string[];
  size: string;
  refreshCadence: string;
  requestFields: readonly string[];
  ctaLabel: string;
  metadataDescription: string;
  placements: readonly { surface: string; placement: string }[];
  emails: readonly { step: string; subject: string; body: string }[];
  successMetric: string;
  adultRules?: readonly string[];
};

export type ResourceBodySection = {
  title: string;
  body: string;
  points?: readonly string[];
};

export type ResourceCalculatorField = {
  id: string;
  label: string;
  help: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  suffix?: string;
};

export type ResourceCalculator = {
  kind: "ai-video-cost-time" | "white-label-margin";
  title: string;
  intro: string;
  primaryCtaLabel: string;
  resultLabels: {
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary: string;
  };
  resultCopy: Record<string, string>;
  disclaimer: string;
  fields: readonly ResourceCalculatorField[];
};

type LeadMagnetDraft = Omit<LeadMagnet, "answerFirst" | "bodySections" | "calculator">;
type LeadMagnetPageCopy = Pick<LeadMagnet, "answerFirst" | "bodySections"> & Pick<Partial<LeadMagnet>, "calculator">;

const commonPlacements = {
  homepage: "Resources strip and final proof/resources section.",
  service: "Relevant service page resource card and contact helper link.",
  industry: "Relevant industry page and cluster hub.",
  blog: "Relevant insights and article closer.",
  footer: "Resources footer group.",
} as const;

const adultRules = [
  "Lawful 18+ only.",
  "Single-email request path; no automated public audit.",
  "Email-only delivery from the founder.",
  "Optional compliance consultation only after the resource is delivered.",
] as const;

const RAW_LEAD_MAGNETS = [
  {
    slug: "2026-white-label-agency-playbook",
    path: "/resources/2026-white-label-agency-playbook",
    title: "The 2026 White-Label Agency Playbook",
    format: "PDF playbook",
    persona: "Agency owners and operators",
    promise: "A practical operating model for adding SEO, GEO/AEO, video, and reporting capacity without hiring ahead of demand.",
    inside: [
      "Offer map for resellable SEO, GEO/AEO, AI video, and ORM work.",
      "Handoff checklist for invisible, co-branded, and specialist-led delivery.",
      "Reporting cadence that protects client trust without noisy dashboards.",
    ],
    size: "12-18 pages",
    refreshCadence: "Annual review with quarterly notes",
    requestFields: ["Work email", "Agency website", "Services you resell"],
    ctaLabel: "Request the playbook",
    metadataDescription:
      "Request SEOH's 2026 White-Label Agency Playbook for agency owners adding SEO, GEO/AEO, AI video, ORM, and reporting capacity.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/white-label and /services/white-label-seo." },
      { surface: "Industry pages", placement: "Agency and professional services paths." },
      { surface: "Blog", placement: "White-label and agency operations articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your SEOH white-label agency playbook",
        body: "Here is the playbook. Use it to decide which work should stay in-house, which work can be white-labeled, and what needs a senior review path.",
      },
      {
        step: "Value follow-up",
        subject: "A useful first cut for white-label fit",
        body: "Start with the services you already sell and mark where delivery, reporting, or technical review is the current bottleneck.",
      },
      {
        step: "Optional invite",
        subject: "Want Menashe to review the fit?",
        body: "If the model looks useful, send the services you resell, monthly volume, and client-facing expectations for a scoped reply.",
      },
    ],
    successMetric: "Playbook requests that become white-label scoping conversations.",
  },
  {
    slug: "geo-aeo-readiness-checklist",
    path: "/resources/geo-aeo-readiness-checklist",
    title: "GEO/AEO Readiness Checklist",
    format: "Checklist",
    persona: "SEO leads, founders, and agency strategists",
    promise: "A quick way to see whether pages are ready for AI answers, citations, and answer-led search.",
    inside: [
      "Entity clarity checks for brand, service, audience, and proof.",
      "Answer-led content checks for TL;DR, definitions, FAQs, and comparisons.",
      "Schema and internal-link checks that support retrieval.",
    ],
    size: "2-4 pages",
    refreshCadence: "Quarterly",
    requestFields: ["Work email"],
    ctaLabel: "Request the checklist",
    metadataDescription:
      "Request SEOH's GEO/AEO Readiness Checklist for AI-search entity clarity, answer-led content, schema, and internal-link checks.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/geo-aeo-ai-search and AI-search platform spokes." },
      { surface: "Industry pages", placement: "All SEO cluster hubs." },
      { surface: "Blog", placement: "AI search and SEO articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your GEO/AEO readiness checklist",
        body: "Here is the checklist. Use it to review entity clarity, answer structure, schema support, and claim context before deeper AI-search work.",
      },
      {
        step: "Value follow-up",
        subject: "The fastest GEO/AEO check",
        body: "Pick one money page and answer the top buyer question in 60 words near the top. That usually reveals the rest of the gap.",
      },
      {
        step: "Optional invite",
        subject: "Want a manual AI visibility review?",
        body: "Send the site, market, and 3 competitors if you want Menashe to scope a manual AI visibility audit.",
      },
    ],
    successMetric: "Checklist requests that lead to manual AI visibility audit requests.",
  },
  {
    slug: "ai-overviews-visibility-audit-template",
    path: "/resources/ai-overviews-visibility-audit-template",
    title: "AI Overviews Visibility Audit Template",
    format: "Template",
    persona: "SEO managers and content strategists",
    promise: "A structured worksheet for checking whether priority pages are ready for Google AI Overviews style summaries.",
    inside: [
      "Prompt and query set for priority service and industry pages.",
      "Source-readiness fields for entities, proof, schema, and FAQs.",
      "Decision notes for rewrite, schema, internal-link, or citation work.",
    ],
    size: "Spreadsheet-style worksheet",
    refreshCadence: "Quarterly",
    requestFields: ["Work email", "Website"],
    ctaLabel: "Request the audit template",
    metadataDescription:
      "Request SEOH's AI Overviews Visibility Audit Template for checking entity clarity, source readiness, schema, and answer-led content.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/geo-aeo-ai-search and /ai-search/google-ai-overviews-optimization." },
      { surface: "Industry pages", placement: "Media, eCommerce, health, and SaaS SEO cluster hubs." },
      { surface: "Blog", placement: "Google AI Overviews articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your AI Overviews audit template",
        body: "Here is the template. Use it to document prompts, page answers, schema coverage, claim support, and next actions.",
      },
      {
        step: "Value follow-up",
        subject: "How to use the AI Overviews template first",
        body: "Run it on one service page, one proof page, and one industry page before expanding the review across the site.",
      },
      {
        step: "Optional invite",
        subject: "Want SEOH to review your AI Overviews gaps?",
        body: "Send the completed first pass or the priority URLs, and Menashe can scope a manual next-step review.",
      },
    ],
    successMetric: "Template requests that produce AI-search audit conversations.",
  },
  {
    slug: "franchise-seo-multi-location-reporting-template",
    path: "/resources/franchise-seo-multi-location-reporting-template",
    title: "Franchise SEO Multi-Location Reporting Template",
    format: "Template",
    persona: "Franchise marketing managers",
    promise: "A reporting structure for separating network, region, and location-level SEO decisions.",
    inside: [
      "Network-level health summary for leadership.",
      "Location-level issue queue for local pages, profiles, and rankings.",
      "Decision log for changes that affect multiple locations.",
    ],
    size: "Spreadsheet and reporting outline",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "Number of locations", "Primary market"],
    ctaLabel: "Request the reporting template",
    metadataDescription:
      "Request SEOH's Franchise SEO Multi-Location Reporting Template for network, region, and location-level SEO reporting.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/franchise-multi-location and local SEO content." },
      { surface: "Industry pages", placement: "Franchise and multi-location paths." },
      { surface: "Blog", placement: "Multi-location SEO articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your franchise SEO reporting template",
        body: "Here is the template. Use it to separate executive summary, regional movement, location-level issues, and decisions needed.",
      },
      {
        step: "Value follow-up",
        subject: "The report line that matters most",
        body: "The best first signal is not only rankings. Track which locations are blocked by profile, page, content, or technical issues.",
      },
      {
        step: "Optional invite",
        subject: "Want a franchise growth check?",
        body: "Send the number of locations, markets, and current reporting stack if you want SEOH to scope the next review.",
      },
    ],
    successMetric: "Template requests that lead to franchise growth checks.",
  },
  {
    slug: "entity-schema-implementation-guide",
    path: "/resources/entity-schema-implementation-guide",
    title: "Entity & Schema Implementation Guide",
    format: "Implementation guide",
    persona: "SEO leads, technical marketers, and agency strategists",
    promise: "A practical guide for turning entity clarity into schema, internal links, and page structure without adding unsupported claims.",
    inside: [
      "Entity inventory fields for brand, service, audience, proof, and geography.",
      "Schema eligibility checks for Organization, Service, FAQ, Article, Product, and BreadcrumbList.",
      "Implementation notes for visible-content alignment and validation cadence.",
    ],
    size: "6-10 pages",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "Website"],
    ctaLabel: "Request the guide",
    metadataDescription:
      "Request SEOH's Entity & Schema Implementation Guide for entity clarity, schema eligibility, internal links, and visible-content alignment.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/geo-aeo-ai-search and /services/white-label-seo." },
      { surface: "Industry pages", placement: "SaaS, eCommerce, health, media, and professional-services paths." },
      { surface: "Blog", placement: "Schema, technical SEO, and AI-search articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your entity and schema implementation guide",
        body: "Here is the guide. Use it to map entities, choose eligible schema types, and keep markup aligned with visible page content.",
      },
      {
        step: "Value follow-up",
        subject: "The schema rule to check first",
        body: "Start by checking whether every schema field is backed by visible content. If it is not visible, it usually needs removal or page-copy support.",
      },
      {
        step: "Optional invite",
        subject: "Want a schema implementation review?",
        body: "Send the site, priority templates, and current schema types if you want Menashe to scope a technical review.",
      },
    ],
    successMetric: "Guide requests that lead to schema and AI-search implementation reviews.",
  },
  {
    slug: "ai-citation-tracking-template",
    path: "/resources/ai-citation-tracking-template",
    title: "AI-Citation Tracking Template",
    format: "Template",
    persona: "SEO leads, publishers, and agency reporting owners",
    promise: "A lightweight way to track AI-search prompts, cited sources, missing owned pages, and follow-up actions over time.",
    inside: [
      "Prompt-set fields for brand, category, comparison, and problem queries.",
      "Citation snapshot fields for source, answer wording, owned-page presence, and risk notes.",
      "Decision log for content, schema, internal-link, and reputation follow-up.",
    ],
    size: "Spreadsheet-style tracker",
    refreshCadence: "Quarterly",
    requestFields: ["Work email", "Website"],
    ctaLabel: "Request the tracking template",
    metadataDescription:
      "Request SEOH's AI-Citation Tracking Template for prompt snapshots, cited sources, owned-page gaps, and AI-search follow-up actions.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/geo-aeo-ai-search and AI-search platform spokes." },
      { surface: "Industry pages", placement: "Media, SaaS, eCommerce, health, and professional-services paths." },
      { surface: "Blog", placement: "AI citation, GEO/AEO, and reporting articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your AI-citation tracking template",
        body: "Here is the tracker. Use it to record prompts, cited sources, owned-page gaps, and follow-up decisions without claiming control over AI answers.",
      },
      {
        step: "Value follow-up",
        subject: "How to keep citation tracking useful",
        body: "Use the same prompt set every cycle. The trend matters more than a single answer snapshot.",
      },
      {
        step: "Optional invite",
        subject: "Want a manual AI-citation review?",
        body: "Send the prompt set, market, and priority pages if you want Menashe to scope an AI-search visibility review.",
      },
    ],
    successMetric: "Template requests that become AI-citation baseline reviews.",
  },
  {
    slug: "multi-location-local-seo-audit-template",
    path: "/resources/multi-location-local-seo-audit-template",
    title: "Multi-Location Local SEO Audit Template",
    format: "Template",
    persona: "Multi-location operators, franchise teams, and local SEO owners",
    promise: "An audit template for checking up to 25 locations across profiles, local pages, rankings, reviews, and reporting gaps.",
    inside: [
      "Location-by-location fields for profiles, landing pages, NAP consistency, and local issues.",
      "Priority scoring for technical, content, reputation, and reporting blockers.",
      "Decision notes for network-level fixes versus location-level follow-up.",
    ],
    size: "Spreadsheet-style audit",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "Number of locations", "Primary market"],
    ctaLabel: "Request the local audit template",
    metadataDescription:
      "Request SEOH's Multi-Location Local SEO Audit Template for checking up to 25 locations across profiles, pages, reviews, and reporting gaps.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/franchise-multi-location and /services/white-label-seo." },
      { surface: "Industry pages", placement: "Franchise, hospitality, professional-services, and industrial paths." },
      { surface: "Blog", placement: "Local SEO and multi-location reporting articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your multi-location local SEO audit template",
        body: "Here is the template. Use it to separate profile, page, review, ranking, and reporting issues across locations.",
      },
      {
        step: "Value follow-up",
        subject: "The multi-location audit shortcut",
        body: "Group issues by pattern before fixing one location at a time. Network-level patterns usually decide the highest-leverage work.",
      },
      {
        step: "Optional invite",
        subject: "Want a multi-location SEO review?",
        body: "Send the location count, markets, and current reporting stack if you want Menashe to scope the next review.",
      },
    ],
    successMetric: "Audit template requests that lead to multi-location local SEO scoping.",
  },
  {
    slug: "ecommerce-seo-audit-template",
    path: "/resources/ecommerce-seo-audit-template",
    title: "eCommerce SEO Audit Template",
    format: "Template",
    persona: "eCommerce owners, managers, and agency SEOs",
    promise: "A practical audit path for product pages, category architecture, faceted navigation, schema, and AI-search readiness.",
    inside: [
      "Product and category opportunity fields.",
      "Faceted navigation and crawl-control checks.",
      "Product schema and AI-search answer-readiness checks.",
    ],
    size: "Spreadsheet-style audit",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "Store URL"],
    ctaLabel: "Request the audit template",
    metadataDescription:
      "Request SEOH's eCommerce SEO Audit Template for product pages, category architecture, faceted navigation, schema, and AI search.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/services/white-label-seo." },
      { surface: "Industry pages", placement: "/industries/ecommerce-consumer-brands and eCommerce SEO cluster pages." },
      { surface: "Blog", placement: "eCommerce SEO articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your eCommerce SEO audit template",
        body: "Here is the template. Start with top categories, product-page patterns, filter URLs, schema, and internal links.",
      },
      {
        step: "Value follow-up",
        subject: "The eCommerce SEO audit shortcut",
        body: "Choose one category template and one product template first. If those are weak, scaling the audit becomes much easier to prioritize.",
      },
      {
        step: "Optional invite",
        subject: "Want SEOH to review the store?",
        body: "Send the store URL, platform, and priority categories if you want Menashe to scope an eCommerce SEO review.",
      },
    ],
    successMetric: "Audit template requests that lead to eCommerce SEO scoping.",
  },
  {
    slug: "health-wellness-seo-compliance-checklist",
    path: "/resources/health-wellness-seo-compliance-checklist",
    title: "Health & Wellness SEO Compliance Checklist",
    format: "Checklist",
    persona: "Health, wellness, diagnostics, and medical-adjacent marketers",
    promise: "A claim-aware checklist for health SEO pages that need useful content without unsupported promises.",
    inside: [
      "YMYL risk classification and reviewer ownership checks.",
      "Medical and health schema alignment checks.",
      "AI-answer clarity checks for sensitive pages.",
    ],
    size: "3-5 pages",
    refreshCadence: "Quarterly",
    requestFields: ["Work email", "Website"],
    ctaLabel: "Request the checklist",
    metadataDescription:
      "Request SEOH's Health & Wellness SEO Compliance Checklist for YMYL, schema, E-E-A-T, claims, and AI-answer clarity.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/services/white-label-seo and /geo-aeo-ai-search." },
      { surface: "Industry pages", placement: "/industries/health-wellness and health SEO cluster pages." },
      { surface: "Blog", placement: "Health SEO and compliance-sensitive content articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your health SEO compliance checklist",
        body: "Here is the checklist. Use it to separate approved claims, review ownership, schema eligibility, and content that needs more care.",
      },
      {
        step: "Value follow-up",
        subject: "The first health SEO page to review",
        body: "Start with the page that makes the strongest claim. That page usually shows whether the review process is clear enough.",
      },
      {
        step: "Optional invite",
        subject: "Want a health SEO review?",
        body: "Send the site, page type, and claim concern if you want Menashe to scope a health SEO review.",
      },
    ],
    successMetric: "Checklist requests that become health SEO compliance reviews.",
  },
  {
    slug: "b2b-saas-geo-aeo-visibility-audit",
    path: "/resources/b2b-saas-geo-aeo-visibility-audit",
    title: "B2B SaaS GEO/AEO Visibility Audit",
    format: "Web report template",
    persona: "B2B SaaS founders and growth marketers",
    promise: "A SaaS-specific AI-search audit path for feature pages, comparison pages, integrations, entities, and buying questions.",
    inside: [
      "Feature and use-case answer-readiness checks.",
      "Comparison and alternative-query review fields.",
      "Entity and schema gap notes for SaaS buying journeys.",
    ],
    size: "6-10 page report outline",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "SaaS URL", "Primary competitors"],
    ctaLabel: "Request the SaaS audit outline",
    metadataDescription:
      "Request SEOH's B2B SaaS GEO/AEO Visibility Audit for feature pages, comparison content, integrations, entities, and AI-search prompts.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/geo-aeo-ai-search and /services/white-label-seo." },
      { surface: "Industry pages", placement: "/industries/saas-tech." },
      { surface: "Blog", placement: "SaaS SEO and AI-search articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your SaaS GEO/AEO audit outline",
        body: "Here is the outline. Use it to review feature pages, comparison pages, integration pages, entity clarity, and AI-search prompts.",
      },
      {
        step: "Value follow-up",
        subject: "The SaaS page to test first",
        body: "Start with the page that answers 'best tool for...' or 'alternative to...' because AI-search comparisons often expose unclear positioning.",
      },
      {
        step: "Optional invite",
        subject: "Want a SaaS AI-search review?",
        body: "Send the SaaS URL and competitors if you want Menashe to scope a manual visibility audit.",
      },
    ],
    successMetric: "Audit outline requests that produce SaaS GEO/AEO scoping conversations.",
  },
  {
    slug: "hospitality-travel-seo-playbook",
    path: "/resources/hospitality-travel-seo-playbook",
    title: "Hospitality & Travel SEO Playbook",
    format: "PDF playbook",
    persona: "Hospitality, events, venue, travel, and local-experience marketers",
    promise: "A local and seasonal SEO playbook for turning maps, reviews, service pages, and event/travel intent into clearer enquiries.",
    inside: [
      "Seasonal demand and local-intent mapping fields.",
      "Venue, event, itinerary, review, and FAQ page checks.",
      "Booking-funnel notes for search, maps, AI answers, and follow-up.",
    ],
    size: "8-12 pages",
    refreshCadence: "Annual review with seasonal notes",
    requestFields: ["Work email", "Website", "Primary location"],
    ctaLabel: "Request the playbook",
    metadataDescription:
      "Request SEOH's Hospitality & Travel SEO Playbook for local visibility, seasonal demand, maps, reviews, service pages, and booking-funnel SEO.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/services/white-label-seo and /geo-aeo-ai-search." },
      { surface: "Industry pages", placement: "/industries/hospitality-events-travel." },
      { surface: "Blog", placement: "Local SEO, hospitality SEO, and seasonal search articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your hospitality and travel SEO playbook",
        body: "Here is the playbook. Use it to map local demand, seasonal pages, review surfaces, and booking-funnel gaps.",
      },
      {
        step: "Value follow-up",
        subject: "The hospitality SEO page to check first",
        body: "Start with the page that should convert high-intent local or seasonal demand. It usually reveals the map, review, and FAQ gaps.",
      },
      {
        step: "Optional invite",
        subject: "Want a hospitality visibility review?",
        body: "Send the site, primary location, and seasonality concern if you want Menashe to scope a manual review.",
      },
    ],
    successMetric: "Playbook requests that become hospitality or travel SEO scoping conversations.",
  },
  {
    slug: "18-plus-compliance-marketing-checklist",
    path: "/resources/18-plus-compliance-marketing-checklist",
    title: "18+ Compliance Marketing Checklist",
    format: "Checklist",
    persona: "Lawful 18+ operators and agencies",
    promise: "A brand-safe checklist for adult-vertical marketing conversations before search, AI visibility, social, or reputation work begins.",
    inside: [
      "Lawful 18+, consent, likeness, platform, and payment-rule checks.",
      "Founder-led compliance review for adult-vertical inquiries.",
      "Professional public-presentation and approval requirements.",
    ],
    size: "2-4 pages",
    refreshCadence: "Quarterly",
    requestFields: ["Email"],
    ctaLabel: "Request the checklist",
    metadataDescription:
      "Request SEOH's 18+ Compliance Marketing Checklist for lawful adult-vertical marketing, consent, likeness, platform, and payment rules.",
    placements: [
      { surface: "Homepage", placement: "Resources card only; no adult hero placement." },
      { surface: "Service pages", placement: "/adult and adult sub-pages only." },
      { surface: "Industry pages", placement: "Adult vertical paths only." },
      { surface: "Blog", placement: "Compliance-first adult-safe articles only." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your 18+ compliance marketing checklist",
        body: "Here is the checklist. Use it before discussing adult-vertical search, AI visibility, social, reputation, avatar, or video work.",
      },
      {
        step: "Value follow-up",
        subject: "The adult-vertical check that comes first",
        body: "Confirm lawful 18+ status, consent, likeness rights, platform constraints, and payment rules before any campaign plan.",
      },
      {
        step: "Optional invite",
        subject: "Want a founder-led compliance consultation?",
        body: "If the checklist raises questions, send the business type, platforms, and compliance concern for a manual founder review.",
      },
    ],
    successMetric: "Checklist requests that become adult compliance consultations.",
    adultRules,
  },
  {
    slug: "fan-platform-growth-playbook",
    path: "/resources/fan-platform-growth-playbook",
    title: "Fan-Platform Growth Playbook",
    format: "PDF playbook",
    persona: "Lawful 18+ operators, agencies, and creator-platform managers",
    promise: "An operator-focused playbook for reviewing search, reputation, referral, and platform-policy context before fan-platform growth work.",
    inside: [
      "Lawful 18+, consent, likeness, platform, and payment-rule pre-checks.",
      "Search, reputation, profile clarity, and off-platform support prompts.",
      "Professional public-presentation and founder-led review requirements.",
    ],
    size: "6-10 pages",
    refreshCadence: "Quarterly",
    requestFields: ["Email"],
    ctaLabel: "Request the playbook",
    metadataDescription:
      "Request SEOH's Fan-Platform Growth Playbook for lawful 18+ operators, covering search, reputation, referral paths, and platform-policy checks.",
    placements: [
      { surface: "Homepage", placement: "Resources card only; no adult hero placement." },
      { surface: "Service pages", placement: "/adult and fan-platform adult sub-pages only." },
      { surface: "Industry pages", placement: "Adult vertical paths only." },
      { surface: "Blog", placement: "Compliance-first adult-safe and fan-platform articles only." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your fan-platform growth playbook",
        body: "Here is the playbook. Use it to review lawful status, profile clarity, search support, reputation risk, and platform-policy constraints.",
      },
      {
        step: "Value follow-up",
        subject: "The fan-platform growth check that comes first",
        body: "Confirm consent, likeness rights, platform terms, payment constraints, and public-presentation boundaries before planning acquisition work.",
      },
      {
        step: "Optional invite",
        subject: "Want a founder-led fan-platform review?",
        body: "Send the business type, platform context, and compliance concern for a manual founder review.",
      },
    ],
    successMetric: "Playbook requests that become adult-safe fan-platform consultations.",
    adultRules,
  },
  {
    slug: "ai-video-cost-time-calculator",
    path: "/resources/ai-video-cost-time-calculator",
    title: "AI Video Cost & Time Calculator",
    format: "Interactive calculator",
    persona: "Creative directors, agencies, and growth teams",
    promise: "An on-page estimator for planning AI video effort by use case, length, language, review flow, and channel.",
    inside: [
      "Inputs for script, voice, avatar, format, localization, and review rounds.",
      "Channel fit notes for ads, landing pages, short-form, and explainers.",
      "Risk checks for likeness, claims, and approvals.",
    ],
    size: "On-page calculator plus worksheet-style follow-up",
    refreshCadence: "Semiannual",
    requestFields: ["Work email", "Use case"],
    ctaLabel: "Request a calculator review",
    metadataDescription:
      "Use SEOH's AI Video Cost & Time Calculator to estimate AI video scope, localization, review rounds, and channel fit before requesting a quote.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/ai-video-production and /ai-avatar-marketing." },
      { surface: "Industry pages", placement: "Agency, creator, eCommerce, and adult-safe paths where relevant." },
      { surface: "Blog", placement: "AI video and avatar workflow articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your AI video cost and time calculator",
        body: "Here is the worksheet. Use it to estimate scope drivers before requesting a sample or quote.",
      },
      {
        step: "Value follow-up",
        subject: "The AI video estimate input most teams miss",
        body: "Review rounds usually change the timeline more than rendering. Decide who approves script, voice, visual style, and compliance before production.",
      },
      {
        step: "Optional invite",
        subject: "Want an AI video sample path?",
        body: "Send the use case, language, channel, and review owner if you want SEOH to scope a sample.",
      },
    ],
    successMetric: "Calculator requests that lead to AI video sample requests.",
  },
  {
    slug: "senior-solo-seo-hiring-brief",
    path: "/resources/senior-solo-seo-hiring-brief",
    title: "The Senior Solo SEO Hiring Brief",
    format: "Brief",
    persona: "Founders, agency owners, and marketing leaders",
    promise: "A decision brief for choosing between an agency, a freelancer, a senior solo specialist, and in-house hiring.",
    inside: [
      "Decision criteria for strategy, execution, reporting, and risk.",
      "When a senior solo operator is enough and when a team is needed.",
      "Questions to ask before handing over SEO, GEO/AEO, or ORM work.",
    ],
    size: "5-7 pages",
    refreshCadence: "Annual",
    requestFields: ["Work email"],
    ctaLabel: "Request the hiring brief",
    metadataDescription:
      "Request SEOH's Senior Solo SEO Hiring Brief for comparing agency, freelancer, senior solo specialist, and in-house SEO options.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/about, /white-label, /services, and comparison pages." },
      { surface: "Industry pages", placement: "Professional services, SaaS, and agency paths." },
      { surface: "Blog", placement: "Hiring and SEO operations articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your senior solo SEO hiring brief",
        body: "Here is the brief. Use it to compare agency, freelancer, senior solo, and in-house options against your real constraints.",
      },
      {
        step: "Value follow-up",
        subject: "The simplest hiring filter",
        body: "If the work needs senior diagnosis and tight scope control more than a large delivery bench, a senior solo specialist may fit.",
      },
      {
        step: "Optional invite",
        subject: "Want help choosing the model?",
        body: "Send the service area, urgency, internal capacity, and risk level if you want a scoped recommendation.",
      },
    ],
    successMetric: "Brief requests that become model-fit scoping conversations.",
  },
  {
    slug: "white-label-margin-calculator",
    path: "/resources/white-label-margin-calculator",
    title: "White-Label Margin Calculator",
    format: "Interactive calculator",
    persona: "Agency owners and finance-aware operators",
    promise: "An on-page margin estimator that uses the agency's own resale and workload inputs without publishing SEOH prices.",
    inside: [
      "Inputs for resale price, delivery load, review time, reporting, and risk.",
      "Scenario notes for invisible, co-branded, and specialist-led delivery.",
      "Decision prompts for margin, capacity, and client trust.",
    ],
    size: "On-page calculator plus worksheet-style follow-up",
    refreshCadence: "Annual",
    requestFields: ["Work email", "Agency website", "Services you resell"],
    ctaLabel: "Request a margin review",
    metadataDescription:
      "Use SEOH's White-Label Margin Calculator for agency-owned quote planning, resale assumptions, fulfillment load, and margin checks.",
    placements: [
      { surface: "Homepage", placement: commonPlacements.homepage },
      { surface: "Service pages", placement: "/white-label and /services/white-label-seo." },
      { surface: "Industry pages", placement: "Agency and professional services paths." },
      { surface: "Blog", placement: "White-label operations articles." },
      { surface: "Footer", placement: commonPlacements.footer },
    ],
    emails: [
      {
        step: "Delivery",
        subject: "Your white-label margin calculator",
        body: "Here is the worksheet. It uses your resale assumptions and workload inputs; SEOH does not publish public prices.",
      },
      {
        step: "Value follow-up",
        subject: "The margin input to check twice",
        body: "Review time and reporting time often decide whether a white-label offer is profitable, not only fulfillment cost.",
      },
      {
        step: "Optional invite",
        subject: "Want to scope the white-label model?",
        body: "Send the services you resell, volume, and margin constraints if you want Menashe to review the fit.",
      },
    ],
    successMetric: "Calculator requests that become white-label partnership inquiries.",
  },
] as const satisfies readonly LeadMagnetDraft[];

function resourcePageCopy(answerFirst: string, body: string, fit: string, proof: string): Pick<LeadMagnet, "answerFirst" | "bodySections"> {
  return {
    answerFirst,
    bodySections: [
      {
        title: "What this resource helps decide",
        body,
      },
      {
        title: "When to request the gated file",
        body: fit,
      },
      {
        title: "Proof and review boundaries",
        body: proof,
      },
    ],
  };
}

const LEAD_MAGNET_PAGE_COPY: Record<LeadMagnetDraft["slug"], LeadMagnetPageCopy> = {
  "2026-white-label-agency-playbook": resourcePageCopy(
    "The 2026 White-Label Agency Playbook helps agency owners decide which SEO, GEO/AEO, video, and reporting work can be resold safely before hiring or outsourcing.",
    "Use it to compare service lines, delivery models, reporting expectations, and client handoff risk before adding white-label capacity.",
    "Request the playbook when the agency already sells growth work and needs a cleaner operating model for specialist fulfillment.",
    "The page stays indexable with the decision framework visible; private examples, worksheets, and operational details are sent only after a manual request.",
  ),
  "geo-aeo-readiness-checklist": resourcePageCopy(
    "The GEO/AEO Readiness Checklist helps teams inspect whether a page is answer-ready for AI Overviews, ChatGPT, Perplexity, Gemini, and Bing Copilot style discovery.",
    "Use it to review entity clarity, answer blocks, schema, internal links, citations, and unsupported claim risk on one priority page before scaling.",
    "Request the checklist when you want a compact audit artifact that can be reused across service, industry, and proof pages.",
    "The public page explains the method without promising inclusion, rankings, citations, or AI answer wording.",
  ),
  "ai-overviews-visibility-audit-template": resourcePageCopy(
    "The AI Overviews Visibility Audit Template helps SEO teams document prompts, page answers, source readiness, schema, and rewrite decisions for Google-style AI summaries.",
    "Use it when one service, proof, or industry page needs a structured first pass before a larger AI-search audit.",
    "Request the template if your team needs the worksheet fields and follow-up sequence rather than only the public overview.",
    "The template supports review discipline; it does not guarantee that Google will show an AI Overview or select any specific source.",
  ),
  "franchise-seo-multi-location-reporting-template": resourcePageCopy(
    "The Franchise SEO Multi-Location Reporting Template helps marketing teams separate network, regional, and location-level SEO decisions.",
    "Use it to keep leadership summaries, local issue queues, and market-level next actions from collapsing into one noisy report.",
    "Request the template when location count, market variation, or reporting ownership makes local visibility hard to compare.",
    "The public page shows the reporting logic; client-specific locations, dashboards, and performance numbers stay private.",
  ),
  "entity-schema-implementation-guide": resourcePageCopy(
    "The Entity & Schema Implementation Guide helps teams turn entity clarity into schema, page structure, and internal links without adding unsupported claims.",
    "Use it to decide which entities belong on the page, which schema types are eligible, and which fields need visible-copy support.",
    "Request the guide when a technical SEO, AI-search, or content team needs a reusable implementation checklist.",
    "The guide keeps schema tied to visible content and avoids using markup to make claims the page itself does not support.",
  ),
  "ai-citation-tracking-template": resourcePageCopy(
    "The AI Citation Tracking Template helps teams record which pages, brands, and third-party sources appear across AI answer surfaces.",
    "Use it to separate prompt families, answer surfaces, cited sources, wrong facts, missing facts, and recommended owned-content fixes.",
    "Request the template when screenshots and anecdotes are no longer enough for AI-search reporting.",
    "Citation tracking is observational; SEOH does not claim control over answer-engine source selection.",
  ),
  "multi-location-local-seo-audit-template": resourcePageCopy(
    "The Multi-Location Local SEO Audit Template helps teams review location pages, profiles, categories, reviews, and market-level blockers.",
    "Use it before rewriting location pages or changing profiles so the work is prioritized by actual local-visibility constraints.",
    "Request the template when local SEO work needs a repeatable issue queue across many branches, clinics, campuses, or stores.",
    "The public page keeps the audit model visible while account access, private profile data, and location performance remain off-page.",
  ),
  "ecommerce-seo-audit-template": resourcePageCopy(
    "The eCommerce SEO Audit Template helps consumer brands review product, category, filter, schema, content, and AI-search readiness.",
    "Use it to separate crawl/indexation problems from content gaps, product proof issues, and category-level demand planning.",
    "Request the template when storefront SEO decisions need a shared worksheet for technical, merchandising, and content stakeholders.",
    "The template avoids generic growth promises and keeps product or revenue data out of public copy.",
  ),
  "health-wellness-seo-compliance-checklist": resourcePageCopy(
    "The Health & Wellness SEO Compliance Checklist helps teams review search visibility without overextending medical, supplement, or wellness claims.",
    "Use it to check claim support, disclaimers, author review, schema, local pages, and content boundaries before publishing.",
    "Request the checklist when SEO work touches YMYL-adjacent topics or requires compliance-aware review.",
    "The public page is informational and does not replace legal, medical, or regulatory advice.",
  ),
  "b2b-saas-geo-aeo-visibility-audit": resourcePageCopy(
    "The B2B SaaS GEO/AEO Visibility Audit helps SaaS teams test how AI-search surfaces understand product category, use cases, competitors, and proof.",
    "Use it before expanding comparison pages, feature pages, or integration content into answer-led search assets.",
    "Request the audit when product marketing and SEO need a shared prompt, citation, and entity gap workflow.",
    "The audit framework supports evidence-based content changes and does not promise rankings or AI citations.",
  ),
  "hospitality-travel-seo-playbook": resourcePageCopy(
    "The Hospitality & Travel SEO Playbook helps teams organize seasonal demand, destination pages, local proof, and experience-led search intent.",
    "Use it to separate evergreen visibility, local pages, event or travel windows, and reputation signals before planning content.",
    "Request the playbook when hospitality or travel work needs a cleaner SEO and AI-search operating path.",
    "The public page avoids performance guarantees and keeps property-specific data, partners, and commercial terms private.",
  ),
  "18-plus-compliance-marketing-checklist": resourcePageCopy(
    "The 18+ Compliance Marketing Checklist helps lawful adult-industry teams review consent, platform rules, payment constraints, and non-explicit public marketing paths.",
    "Use it before planning SEO, AI search, social, avatar, video, or reputation work for lawful 18+ brands.",
    "Request the checklist when compliance context needs founder review before a campaign is scoped.",
    "The public page stays conservative and non-explicit; SEOH does not help bypass platform, payment, consent, or age-verification rules.",
  ),
  "fan-platform-growth-playbook": resourcePageCopy(
    "The Fan Platform Growth Playbook helps lawful creator teams review platform-safe growth without implying guaranteed account approval or reach.",
    "Use it to separate profile positioning, search visibility, social constraints, reputation, and consent documentation before acquisition work.",
    "Request the playbook when a founder-led review is useful before changing public messaging or channel strategy.",
    "The public page is non-explicit and policy-aware; platform terms, consent, likeness rights, and payment constraints stay visible.",
  ),
  "ai-video-cost-time-calculator": {
    ...resourcePageCopy(
      "The AI Video Cost & Time Calculator gives a public planning estimate for AI video scope drivers before a sample or quote request.",
      "Use it to model runtime, format count, localization, review rounds, and complexity before deciding whether the project is a small sample or a larger production workflow.",
      "Request a calculator review when the estimate exposes approval, rights, localization, or channel questions that need manual scoping.",
      "The calculator is a planning tool, not a quote. Final cost and timeline depend on assets, rights, review owners, and approved deliverables.",
    ),
    calculator: {
      kind: "ai-video-cost-time",
      title: "Estimate AI video effort",
      intro: "Adjust the inputs to see the planning range before requesting an SEOH sample path.",
      primaryCtaLabel: "Use the calculator",
      resultLabels: {
        primary: "Planning range",
        secondary: "Scope signal",
        tertiary: "Review risk",
        quaternary: "Next step",
      },
      resultCopy: {
        smallScope: "Small sample scope",
        standardScope: "Standard production scope",
        complexScope: "Complex multi-asset scope",
        lowRisk: "Review load looks light",
        reviewRisk: "Review flow needs naming before production",
        heavyRisk: "Approvals and localization should be scoped manually",
        workingDaysUnit: "working days",
        nextStep: "Send the use case, channel, language count, and review owner for a manual sample recommendation.",
      },
      disclaimer: "This calculator gives a public planning range only. It is not an SEOH quote or delivery commitment.",
      fields: [
        { id: "runtimeMinutes", label: "Finished runtime", help: "Total finished video minutes across the first deliverable.", min: 1, max: 20, step: 1, defaultValue: 3, suffix: "min" },
        { id: "outputFormats", label: "Output formats", help: "Count each distinct aspect ratio or channel format.", min: 1, max: 8, step: 1, defaultValue: 2 },
        { id: "languageVersions", label: "Language versions", help: "Include the original language plus localized variants.", min: 1, max: 8, step: 1, defaultValue: 1 },
        { id: "reviewRounds", label: "Review rounds", help: "Script, voice, visual, compliance, and stakeholder passes.", min: 1, max: 6, step: 1, defaultValue: 2 },
        { id: "complexity", label: "Complexity", help: "1 is a simple asset; 3 includes heavier creative, rights, or compliance review.", min: 1, max: 3, step: 1, defaultValue: 2 },
      ],
    },
  },
  "senior-solo-seo-hiring-brief": resourcePageCopy(
    "The Senior Solo SEO Hiring Brief helps founders and marketing leaders choose between an agency, freelancer, senior solo specialist, or in-house hire.",
    "Use it to compare strategy depth, execution load, reporting needs, risk, and management overhead before picking a delivery model.",
    "Request the brief when the team is deciding whether a senior operator is enough or a larger delivery bench is required.",
    "The brief is a decision aid, not a hiring guarantee or replacement for reference checks and commercial due diligence.",
  ),
  "white-label-margin-calculator": {
    ...resourcePageCopy(
      "The White-Label Margin Calculator gives agencies a public way to test resale price, fulfillment load, internal time, and margin pressure before scoping a partner model.",
      "Use it to identify whether a planned white-label offer has enough margin after fulfillment, review, reporting, and buffer time.",
      "Request a margin review when the calculator shows a thin margin, high internal time, or a service that may need a different resale package.",
      "The calculator uses your inputs and does not publish SEOH prices. Final partner terms require written scope approval.",
    ),
    calculator: {
      kind: "white-label-margin",
      title: "Estimate white-label margin",
      intro: "Use your own resale assumptions to check margin pressure before requesting a white-label review.",
      primaryCtaLabel: "Use the calculator",
      resultLabels: {
        primary: "Estimated monthly gross margin",
        secondary: "Estimated gross margin rate",
        tertiary: "Buffered fulfillment cost",
        quaternary: "Margin signal",
      },
      resultCopy: {
        strongMargin: "Margin looks workable for a scoped white-label offer.",
        watchMargin: "Margin needs review before the offer is sold.",
        thinMargin: "Margin is thin; change price, scope, reporting load, or fulfillment model before selling.",
        nextStep: "Send the services, resale assumptions, expected volume, and reporting obligations for a manual fit review.",
      },
      disclaimer: "This calculator is for agency-owned planning only. It is not an SEOH price list or quote.",
      fields: [
        { id: "resalePrice", label: "Monthly resale price", help: "What the agency expects to charge the end client each month.", min: 500, max: 50000, step: 250, defaultValue: 6000, suffix: "USD" },
        { id: "fulfillmentCost", label: "External fulfillment cost", help: "Expected monthly partner, contractor, or specialist cost.", min: 0, max: 40000, step: 250, defaultValue: 3000, suffix: "USD" },
        { id: "internalHours", label: "Internal agency hours", help: "Monthly account, review, reporting, and client management time.", min: 0, max: 80, step: 1, defaultValue: 8 },
        { id: "hourlyCost", label: "Internal hourly cost", help: "Blended internal cost for account, strategy, and reporting time.", min: 0, max: 300, step: 5, defaultValue: 75, suffix: "USD" },
        { id: "revisionBuffer", label: "Revision buffer", help: "Extra fulfillment buffer for revisions, QA, reporting, and delays.", min: 0, max: 40, step: 1, defaultValue: 10, suffix: "%" },
      ],
    },
  },
};

export const LEAD_MAGNETS = RAW_LEAD_MAGNETS.map((magnet) => ({
  ...magnet,
  ...LEAD_MAGNET_PAGE_COPY[magnet.slug],
})) satisfies readonly LeadMagnet[];

export const LEAD_MAGNET_CARDS = LEAD_MAGNETS.map((magnet) => ({
  id: magnet.slug,
  title: magnet.title,
  summary: magnet.promise,
  href: magnet.path,
  formType: "adultRules" in magnet && magnet.adultRules ? "adult" : "geo-aeo",
  audience: magnet.persona,
})) as readonly {
  id: string;
  title: string;
  summary: string;
  href: string;
  formType: string;
  audience: string;
}[];

export function getLeadMagnet(slug: string): LeadMagnet | undefined {
  return LEAD_MAGNETS.find((magnet) => magnet.slug === slug);
}
